import React from 'react';

interface SearchAppearanceData {
  searchAppearance: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

interface QueryRow {
  query: string;
  page?: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

interface SearchAppearancePanelProps {
  data: SearchAppearanceData[];
  featuredSnippetQueries?: QueryRow[];
  aiOverviewQueries?: QueryRow[];
  isLoading?: boolean;
}

const getAppearanceIcon = (type: string): string => {
  const icons: { [key: string]: string } = {
    '': '🔍',
    'FEATURED_SNIPPET': '⭐',
    'AI_OVERVIEW': '🤖',
    'AMP_BLUE_LINKS': '⚡',
    'AMP_TOP_STORIES': '⚡📰',
    'DISCOVER': '🎯',
    'GOOGLE_NEWS': '📰',
    'IMAGE_SEARCH': '🖼️',
    'JOBS_DETAILS': '💼',
    'JOBS_LISTING': '💼',
    'MERCHANT_LISTINGS': '🛒',
    'NEWS_DESKTOP': '📰',
    'RECIPE_FEATURE': '👨‍🍳',
    'RECIPE_HOST_CAROUSEL': '👨‍🍳',
    'REVIEW_SNIPPET': '⭐',
    'SPECIAL_ANNOUNCEMENT': '📢',
    'VIDEO': '🎥',
    'WEB_LIGHT_RESULT': '🌐',
  };
  return icons[type] || '🔍';
};

const getAppearanceLabel = (type: string): string => {
  const labels: { [key: string]: string } = {
    '': 'Regular Search Result',
    'FEATURED_SNIPPET': 'Featured Snippet',
    'AI_OVERVIEW': 'AI Overview',
    'AMP_BLUE_LINKS': 'AMP Blue Links',
    'AMP_TOP_STORIES': 'AMP Top Stories',
    'DISCOVER': 'Google Discover',
    'GOOGLE_NEWS': 'Google News',
    'IMAGE_SEARCH': 'Image Search',
    'JOBS_DETAILS': 'Jobs Details',
    'JOBS_LISTING': 'Jobs Listing',
    'MERCHANT_LISTINGS': 'Shopping',
    'NEWS_DESKTOP': 'News Desktop',
    'RECIPE_FEATURE': 'Recipe Feature',
    'RECIPE_HOST_CAROUSEL': 'Recipe Carousel',
    'REVIEW_SNIPPET': 'Review Snippet',
    'SPECIAL_ANNOUNCEMENT': 'Special Announcement',
    'VIDEO': 'Video',
    'WEB_LIGHT_RESULT': 'Web Light Result',
  };
  return labels[type] || type || 'Regular Search Result';
};

const isHighValueType = (type: string) => ['FEATURED_SNIPPET', 'AI_OVERVIEW'].includes(type);

const QueryTable: React.FC<{ rows: QueryRow[]; emptyMessage: string }> = ({ rows, emptyMessage }) => {
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500 py-4 px-2">{emptyMessage}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Query</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ranking URL</th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Clicks</th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Impressions</th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">CTR</th>
            <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Position</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.slice(0, 100).map((row, i) => (
            <tr key={i} className="hover:bg-gray-50">
              <td className="px-4 py-2 text-gray-900 max-w-xs">
                <div className="flex items-center gap-2">
                  <span className="truncate">{row.query}</span>
                  <a
                    href={`https://www.google.co.uk/search?q=${encodeURIComponent(row.query)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-1.5 py-0.5 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 flex-shrink-0"
                  >SERP</a>
                </div>
              </td>
              <td className="px-4 py-2 text-blue-600 max-w-xs truncate">
                {row.page ? (
                  <a href={row.page} target="_blank" rel="noopener noreferrer" title={row.page} className="hover:underline">
                    {row.page.replace(/^https?:\/\//, '')}
                  </a>
                ) : <span className="text-gray-400">—</span>}
              </td>
              <td className="px-4 py-2 text-center text-gray-900">{row.clicks.toLocaleString()}</td>
              <td className="px-4 py-2 text-center text-gray-600">{row.impressions.toLocaleString()}</td>
              <td className="px-4 py-2 text-center text-gray-600">{(row.ctr * 100).toFixed(1)}%</td>
              <td className="px-4 py-2 text-center text-gray-600">{row.position.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SearchAppearancePanel: React.FC<SearchAppearancePanelProps> = ({
  data,
  featuredSnippetQueries = [],
  aiOverviewQueries = [],
  isLoading,
}) => {
  const totalClicks = data.reduce((sum, item) => sum + item.clicks, 0);
  const totalImpressions = data.reduce((sum, item) => sum + item.impressions, 0);
  const featuredSnippet = data.find(d => d.searchAppearance === 'FEATURED_SNIPPET');
  const aiOverview = data.find(d => d.searchAppearance === 'AI_OVERVIEW');

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <span className="text-2xl">✨</span>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Featured Snippets & AI Overviews</h2>
            <p className="text-sm text-gray-500">How your content appears in Google's premium search features</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <svg className="animate-spin w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          Loading appearance data...
        </div>
      ) : data.length === 0 ? (
        <div className="px-6 py-10 text-center text-gray-400">No search appearance data found for the selected date range.</div>
      ) : (
        <>
          {/* Highlight cards for Featured Snippet & AI Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 border-b border-gray-100">
            <div className={`rounded-lg p-4 border ${featuredSnippet ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">⭐</span>
                <span className="font-semibold text-gray-800">Featured Snippets</span>
              </div>
              {featuredSnippet ? (
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div><p className="text-gray-500">Clicks</p><p className="font-bold text-gray-900">{featuredSnippet.clicks.toLocaleString()}</p></div>
                  <div><p className="text-gray-500">Impressions</p><p className="font-bold text-gray-900">{featuredSnippet.impressions.toLocaleString()}</p></div>
                  <div><p className="text-gray-500">CTR</p><p className="font-bold text-gray-900">{(featuredSnippet.ctr * 100).toFixed(1)}%</p></div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No featured snippets detected in this period.</p>
              )}
            </div>

            <div className={`rounded-lg p-4 border ${aiOverview ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">🤖</span>
                <span className="font-semibold text-gray-800">AI Overviews</span>
              </div>
              {aiOverview ? (
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div><p className="text-gray-500">Clicks</p><p className="font-bold text-gray-900">{aiOverview.clicks.toLocaleString()}</p></div>
                  <div><p className="text-gray-500">Impressions</p><p className="font-bold text-gray-900">{aiOverview.impressions.toLocaleString()}</p></div>
                  <div><p className="text-gray-500">CTR</p><p className="font-bold text-gray-900">{(aiOverview.ctr * 100).toFixed(1)}%</p></div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No AI Overview appearances detected in this period.</p>
              )}
            </div>
          </div>

          {/* Featured Snippet queries */}
          {featuredSnippetQueries.length > 0 && (
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span>⭐</span> Queries triggering Featured Snippets ({featuredSnippetQueries.length})
              </h3>
              <QueryTable rows={featuredSnippetQueries} emptyMessage="No featured snippet queries found." />
            </div>
          )}

          {/* AI Overview queries */}
          {aiOverviewQueries.length > 0 && (
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <span>🤖</span> Queries triggering AI Overviews ({aiOverviewQueries.length})
              </h3>
              <QueryTable rows={aiOverviewQueries} emptyMessage="No AI Overview queries found." />
            </div>
          )}

          {/* Full appearance breakdown table */}
          <div className="px-6 py-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">All search feature appearances</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Feature Type</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Clicks</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Impressions</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">CTR</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Avg Position</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.sort((a, b) => b.clicks - a.clicks).map((item, index) => (
                    <tr key={index} className={`hover:bg-gray-50 ${isHighValueType(item.searchAppearance) ? 'bg-yellow-50' : ''}`}>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{getAppearanceIcon(item.searchAppearance)}</span>
                          <span className="font-medium text-gray-900">{getAppearanceLabel(item.searchAppearance)}</span>
                          {isHighValueType(item.searchAppearance) && (
                            <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full font-medium">High value</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center text-sm font-medium text-gray-900">{item.clicks.toLocaleString()}</td>
                      <td className="px-3 py-3 text-center text-sm text-gray-600">{item.impressions.toLocaleString()}</td>
                      <td className="px-3 py-3 text-center text-sm text-gray-600">{(item.ctr * 100).toFixed(1)}%</td>
                      <td className="px-3 py-3 text-center text-sm text-gray-600">{item.position.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="px-6 py-3 bg-yellow-50 border-t border-yellow-100">
            <p className="text-xs text-yellow-800">💡 <strong>Featured Snippets</strong> and <strong>AI Overviews</strong> are highly competitive positions — pages earning these have strong topical authority and well-structured content. <strong>Translated Results</strong> means Google automatically translated your page for users searching in a different language, serving it to international audiences without you needing to do any translation work. Google Search Console reports aggregate totals per feature type — individual query breakdowns per appearance type are not available via the GSC API.</p>
          </div>
        </>
      )}
    </div>
  );
};

export default SearchAppearancePanel;
